import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import adminAPI from "./apiClient";
import type { Action, LLMRequest } from "./types";
import { getTotalCostFromApi, formatCost } from "./modelPricing";
import { formatDateWithZone } from "../../utils/formatDate";

export default function ActionDetail(): JSX.Element {
  const { conversationId, turnId, actionId } = useParams<{ conversationId: string; turnId: string; actionId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTab = searchParams.get("fromTab") || "actions";
  const [action, setAction] = useState<Action | null>(null);
  const [llmRequests, setLlmRequests] = useState<LLMRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchActionDetails();
  }, [conversationId, turnId, actionId]);

  const fetchActionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const actionData = await adminAPI.getAction(conversationId, actionId);

      setAction(actionData.action);
      setLlmRequests(actionData.llm_requests);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatJSON = (data: unknown): string => {
    try {
      if (typeof data === 'string') {
        return JSON.stringify(JSON.parse(data), null, 2);
      }
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!action) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Action not found.</div>
      </div>
    );
  }

  const handleBackToTurn = () => {
    navigate(`/admin/conversation/${conversationId}/turn/${turnId}?tab=${fromTab}`);
  };

  return (
    <div className="space-y-6">
      {/* Action Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleBackToTurn}
              className="text-gray-500 hover:text-gray-700 mr-2"
              title="Back to Turn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">Action Details</h2>
            {action.is_decision && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                Agent Decision
              </span>
            )}
          </div>
          {error && (
            <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md">
              Using mock data - API not available
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Action ID</label>
            <p className="mt-1 text-sm font-mono text-gray-900">{action.action_id}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Action Type</label>
            <p className="mt-1 text-sm text-gray-900">{action.action_type}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Created At</label>
            <p className="mt-1 text-sm text-gray-900">{formatDateWithZone(action.created_at)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">LLM Requests</label>
            <p className="mt-1 text-sm text-gray-900">{llmRequests.length}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Action Cost</label>
            <div className="mt-1">
              <p className="text-sm font-medium text-gray-900">
                {formatCost(getTotalCostFromApi(
                  action.total_input_cost_usd,
                  action.total_output_cost_usd
                ))}
              </p>
              {(action.total_prompt_tokens || action.total_completion_tokens) && (
                <p className="text-xs text-gray-500">
                  {((action.total_prompt_tokens || 0) + (action.total_completion_tokens || 0)).toLocaleString()} tokens
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Decision-specific fields */}
        {action.is_decision && (
          <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
            <h3 className="text-lg font-medium text-purple-900">Decision Information</h3>

            {action.selected_action && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-purple-700">Selected Action</label>
                  <p className="mt-1 text-sm font-mono text-purple-900">{action.selected_action}</p>
                </div>
                {action.resolved_action && action.resolved_action !== action.selected_action && (
                  <div>
                    <label className="block text-sm font-medium text-purple-700">Resolved To</label>
                    <p className="mt-1 text-sm font-mono text-purple-900">{action.resolved_action}</p>
                  </div>
                )}
              </div>
            )}

            {action.decision_source && (
              <div>
                <label className="block text-sm font-medium text-purple-700">Decision Source</label>
                <p className="mt-1 text-sm text-purple-900">{action.decision_source}</p>
              </div>
            )}

            {action.decision_parameters && Object.keys(action.decision_parameters).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-2">Decision Parameters</label>
                <div className="bg-white p-3 rounded border border-purple-200">
                  <pre className="text-sm text-purple-900 whitespace-pre-wrap overflow-x-auto">
                    {formatJSON(action.decision_parameters)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {action.rationale && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-500 mb-2">Rationale</label>
            <div className="bg-blue-50 p-4 rounded-md">
              <p className="text-gray-900">{action.rationale}</p>
            </div>
          </div>
        )}
      </div>

      {/* Action Output */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Action Output</h3>
        <div className="bg-gray-50 p-4 rounded-md">
          <pre className="text-sm text-gray-900 whitespace-pre-wrap overflow-x-auto">
            {formatJSON(action.action_output)}
          </pre>
        </div>
      </div>

      {/* LLM Requests */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            LLM Requests ({llmRequests.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200">
          {llmRequests.map((request, index) => (
            <div key={request.llm_request_id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full">
                    <span className="text-sm font-medium text-purple-600">{index + 1}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Request {request.llm_request_id}</p>
                    <p className="text-sm text-gray-500">
                      {request.model} • {formatDateWithZone(request.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {(request.input_cost_usd || request.output_cost_usd) && (
                    <div className="text-right">
                      <div className="text-sm font-medium text-green-700">
                        {formatCost(getTotalCostFromApi(request.input_cost_usd, request.output_cost_usd))}
                      </div>
                      {(request.prompt_tokens || request.completion_tokens) && (
                        <div className="text-xs text-gray-500">
                          {((request.prompt_tokens || 0) + (request.completion_tokens || 0)).toLocaleString()} tokens
                        </div>
                      )}
                    </div>
                  )}
                  <a
                    href={`/admin/conversation/${conversationId}/turn/${turnId}/action/${actionId}/llm-request/${request.llm_request_id}?fromTab=${fromTab}`}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Details →
                  </a>
                </div>
              </div>

              {/* Prompt Preview */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-500 mb-2">Prompt Preview</label>
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-900">
                    {request.prompt.length > 200
                      ? `${request.prompt.substring(0, 200)}...`
                      : request.prompt
                    }
                  </p>
                </div>
              </div>

              {/* Response Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Response Preview</label>
                <div className="bg-blue-50 p-3 rounded-md">
                  <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                    {typeof request.response === 'string' && request.response.length > 300
                      ? `${request.response.substring(0, 300)}...`
                      : formatJSON(request.response).substring(0, 300) + (formatJSON(request.response).length > 300 ? '...' : '')
                    }
                  </pre>
                </div>
              </div>
            </div>
          ))}
        </div>

        {llmRequests.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">No LLM requests found for this action.</div>
          </div>
        )}
      </div>
    </div>
  );
}